import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useGetDashboardVersionsQuery } from '../../../api/dashboardApiSlice';
import versionCompare from '../../../services/versionCompare';
import Block from '../../Block';
import { Loader } from '../../components';
import FilterButton, { FilterGroup } from '../../FilterButton';
import DevicesCell from '../../metrics/tables/cells/DevicesCell';
import OptInRateCell from '../../metrics/tables/cells/OptInRateCell';
import { OsIcon } from '../../OsIcon';
import Table from '../../tables/Table';
import Pagination from '../../tables/Pagination';
import TableSearch from '../../tables/TableSearch';
import useCustomTable from '../../tables/useCustomTable';

const SdkVersionTable = ({filter, onRowClick}) => {
  const { t, i18n } = useTranslation()
  const {
    tableProps,
    paginationProps,
    tableFilterProps,
    tableSearchProps
  } = useCustomTable({
    sortable: true,
    paginate: true,
    hiddenColumns: ['environment'],
    initialFilters: [{id: "environment", value: ["production"]}],
    sortBy: {sdk_version: 'desc'}
  });
  const history = useHistory();
  const formatAppName = (app) => ( app.name ?? app.bundle_id )

  const toFloat = (val) => {
    if (isNaN(val)) { return -1 }
    return parseFloat(val)
  }

  const columns = useMemo(() => [{
    Header: t("app.sdk_version"),
    accessor: 'sdk_version',
    sortType: (rowA, rowB, id, desc) => (versionCompare(rowB.original.sdk_version,rowA.original.sdk_version))
  },{
    Header: t("publisher.app.os"),
    Cell: ({row}) => (<OsIcon os={row.original.os} />),
    accessor: 'os',
  },{
    Header: t("publisher.app.environment"),
    accessor: 'environment'
  },{
    Header: t("publisher.app.test_sdk"),
    accessor: 'test_date',
    format: 'date'
  },{
    Header: t("publisher.app.production_sdk"),
    accessor: 'production_date',
    format: 'date'
  },{
    Header: t("stats.devices"),
    Cell: ({row}) => (<DevicesCell devices={row.original.active_devices} refDevices={row.original.ref_active_devices} />),
    accessor: 'active_devices',
  },{
    Header: t("stats.geolocated_devices"),
    Cell: ({row}) => (<DevicesCell devices={row.original.location_authorized_devices} refDevices={row.original.ref_location_authorized_devices} />),
    accessor: 'location_authorized_devices',
  },{
    Header: t("stats.opt_in_geolocated_devices"),
    Cell: ({row}) => (
        <OptInRateCell val={row.original.opt_in_geolocated_devices} refVal={row.original.ref_opt_in_geolocated_devices} />
      ),
    accessor: 'opt_in_geolocated_devices',
    sortType: (rowA, rowB, id, desc) => (Math.sign(toFloat(rowB.original.opt_in_geolocated_devices) - toFloat(rowA.original.opt_in_geolocated_devices)))
  },{
    Header: t("stats.consent"),
    Cell: ({row}) => (<DevicesCell devices={row.original.loc_consented_devices} refDevices={row.original.ref_loc_consented_devices} />),
    accessor: 'loc_consented_devices',
  },{
    Header: t("stats.consent_rate"),
    Cell: ({row}) => (<OptInRateCell val={row.original.loc_consent_rate} refVal={row.original.ref_loc_consent_rate} />),
    accessor: 'loc_consent_rate',
    sortType: (rowA, rowB, id, desc) => (Math.sign(toFloat(rowB.original.loc_consent_rate) - toFloat(rowA.original.loc_consent_rate)))
  },{
    Header: t("stats.prev_locations_uu"),
    Cell: ({row}) => (<DevicesCell  devices={row.original.prev_locations} refDevices={row.original.ref_locations}  />),
    accessor: 'prev_locations',
  },{
    Header: t("stats.locations_dau"),
    Cell: ({row}) => (<DevicesCell  devices={row.original.locations_dau} refDevices={row.original.ref_locations_dau}  />),
    accessor: 'locations_dau',
  },{
    Header: t("stats.locations_uu_30"),
    Cell: ({row}) => (<DevicesCell  devices={row.original.locations_uu_30_days} refDevices={row.original.ref_locations_uu_30_days}  />),
    accessor: 'locations_uu_30_days',
  }]);
  const { data = [], isLoading, isSuccess, isError, refetch } = useGetDashboardVersionsQuery({filter})
  const tableData = useMemo(()=>{
    return data.map((row) => {
      return {
        ...row,
        environment: row.production_date ? "production" : "test",
        opt_in_geolocated_devices: row.location_authorized_devices/row.active_devices,
        ref_opt_in_geolocated_devices: row.ref_location_authorized_devices/row.ref_active_devices,
        loc_consent_rate: row.loc_consented_devices/row.location_authorized_devices,
        ref_loc_consent_rate: row.ref_loc_consented_devices/row.ref_location_authorized_devices,
        consent_rate: row.location_authorized_devices/row.consented_devices,
        ref_consent_rate: row.ref_location_authorized_devices/row.ref_consented_devices,
      }
    })
  }, [data])

  return (
    <>
      <FilterGroup {...tableFilterProps}>
        <FilterButton
            title={t("publisher.environment")}
            options={["Created", "Test", "Production"]}
            search={false}
            includeAll={true}
            selected={["Production"]}
            name="environment"/>
        <TableSearch {...tableSearchProps} style={{float: "right"}}/>
      </FilterGroup>
      <Block title={t("dashboard.sdkVersion")}>
        <Loader success={!isLoading && isSuccess} error={!isLoading && isError} retry={refetch}>
          <Table
            data={tableData}
            columns={columns}
            {...tableProps}
            onRowClick={onRowClick} />
        </Loader>
        <Pagination {...paginationProps}/>
      </Block>
    </>
  );
}

export default SdkVersionTable;
